// hooks/market/useMyShops.ts
//
// Gère les boutiques dont l'utilisateur connecté est propriétaire
// (section "Mes boutiques" de reglages.tsx, userpro uniquement), et la
// mise à jour de leur ui_config (couleurs, typographie, layout, etc.
// via l'écran d'apparence + ColorPickerField).
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'get-my-shops'
//   POST {API_BASE_URL}/market   action: 'update-shop-ui-config', { shop_id, ui_config }
// (voir backend/handleMarket.ts → getMyShops / updateShopUiConfig)
//
// IMPORTANT : updateUiConfig envoie TOUJOURS l'objet ui_config complet
// (jamais un patch partiel) — la contrainte CHECK SQL exige que toutes
// les sections/clés soient présentes à chaque écriture.

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';
import type { ShopUiConfig } from '@/lib/market/uiConfigDefaults';

export type MyShop = {
  id: string;
  name: string;
  slug: string;
  tagline: string | null;
  cover_image_url: string | null;
  status: 'draft' | 'published' | 'disabled' | 'archived';
  is_verified: boolean;
  ui_config: ShopUiConfig;
  published_at: string | null;
  created_at: string;
};

export function useMyShops() {
  const [shops, setShops] = useState<MyShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [saving, setSaving]   = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchMyShops = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-my-shops',
          access_token: token,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger vos boutiques');
        return;
      }

      setShops(data.shops || []);
    } catch (err) {
      console.error('[useMyShops]', err);
      setError('network_error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyShops();
  }, [fetchMyShops]);

  // Met à jour ui_config d'une boutique. Retourne true en cas de succès,
  // false sinon (l'appelant peut alors afficher saveError).
  const updateUiConfig = useCallback(async (shopId: string, nextConfig: ShopUiConfig): Promise<boolean> => {
    setSaving(true);
    setSaveError(null);

    // Mise à jour optimiste locale
    const previousShops = shops;
    setShops(prev => prev.map(s => s.id === shopId ? { ...s, ui_config: nextConfig } : s));

    try {
      const token = await getValidToken();
      if (!token) {
        setShops(previousShops);
        setSaveError('session_expired');
        return false;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'update-shop-ui-config',
          access_token: token,
          shop_id:      shopId,
          ui_config:    nextConfig,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setShops(previousShops);
        setSaveError(data.error || "Impossible d'enregistrer l'apparence");
        return false;
      }

      return true;
    } catch (err) {
      console.error('[useMyShops][updateUiConfig]', err);
      setShops(previousShops);
      setSaveError('network_error');
      return false;
    } finally {
      setSaving(false);
    }
  }, [shops]);

  return { shops, loading, error, refresh: fetchMyShops, updateUiConfig, saving, saveError };
}
